import classNames from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import { Label, Typography } from "components/atoms";
import { InstructorMobile } from "./InstructorListMobile";
import { decodeToken } from "utils/functions/decodeToken";
import { formatToLocalDate } from "utils/functions/formatToLocalDate";
import { useGetOverallProgressQuery, homeApi } from "services/homeApi";
import { InstructorListButtonMobile } from "./InstructorListButtonMobile";
import { Spinner } from "components/atoms/Icon";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_PROGRAME, SELF_PACED_PROGRAME } from "utils/constants";
import { openExternalUrl } from "utils/functions/openExternalUrl";
import { useAppDispatch } from "hooks";
import { RefreshModal } from "components/molecules/RefreshModal";
import "./index.scss";

interface TestData {
  testName: string;
  Name?: string;
  dueDate: string;
  ClassDate?: string;
  DateCompleted?: string;
  completedOn: string;
  TimeCompleted?: string;
  TimeEntered?: string;
  Status: string;
  InstructorName?: string;
}

interface IProps {
  data?: TestData[];
  type: string;
  theme: "light" | "dark";
}

export const ProgressSummaryMobileTestDetails = ({
  data,
  type,
  theme,
}: IProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const payload = decodeToken();

  const userId = payload ? payload.userid : null;
  const productCode = payload ? payload.productcode : null;
  const offeringCode = payload ? payload.offeringcode : null;

  const isDashboard =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === DASHBOARD_PROGRAME;

  const isSelfPaced =
    payload &&
    payload.hasOwnProperty("programtype") &&
    payload.programtype === SELF_PACED_PROGRAME;

  const { isFetching, refetch: refetchOverallProgress } = useGetOverallProgressQuery({
    Userid: userId,
    Productcode: productCode,
    Offeringcode: offeringCode,
  });

  const [isRefreshModalOpen, setIsRefreshModalOpen] = useState({
    isOpen: false,
    data: "",
  });

  const [contentObjectType, setContentObjectType] = useState<string | undefined>("");

  const handleRefreshModalOpen = (item: any) => {
    const contentType =
      item.ContentObjectType ||
      item.ContentObject?.ContentObjectType ||
      item.Type ||
      "Test";
    setContentObjectType(contentType);
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: true,
      data: contentType,
    }));
  };

  const handleRefreshModalClose = () => {
    setIsRefreshModalOpen((prev) => ({
      ...prev,
      isOpen: false,
      data: "",
    }));
  };

  const handleRefresh = async () => {
    const promises = [
      refetchOverallProgress && refetchOverallProgress(),
    ];

    Promise.allSettled(promises).then(() => {
      handleRefreshModalClose();
    });
    
    // Invalidate cache to ensure fresh data
    dispatch(homeApi.util.invalidateTags(["OverallProgress"]));
  };

  const getMatchColor = (status: string) => {
    switch (status) {
      case "Past Due":
      case "Time Limit Exceeded":
      case "Not Attended":
        return "red";
      case "Completed":
      case "Complete":
      case "Attended":
      case "Viewed Recording":
      case "Watched Recording":
        return "green";
      case "In Progress":
      case "In-Progress":
        return "light-yellow";
      case "Locked":
      case "locked":
        return "dark-gray";
      default:
        return "gray";
    }
  };

  const getTitle = (type: string) => {
    switch (type) {
      case "Diagnostics":
        return "Diagnostic Exam Name";
      case "Homework":
        return "Homework Name";
      case "Tests":
        return "Test Name";
      case "Attendance":
      case "TopicFocus":
        return "Class Name";
      default:
        return "Diagnostic Exam Name";
    }
  };

  const handleItemClick = (item: any, type: any) => {
    if (item.hasOwnProperty("Status") && item.Status.toLowerCase() === "locked") {
      navigate("/self-paced/unlock");
    } else if (type !== "Attendance") {
      if (
        item.ContentObjectType === "OfflineDrill" ||
        item.ContentObjectType === "OfflineReading" ||
        (item.ContentObject &&
          item.ContentObject.ContentObjectType === "OfflineDrill") ||
        (item.ContentObject &&
          item.ContentObject.ContentObjectType === "OfflineReading")
      ) {
        if(isDashboard) {
          navigate("/drill", { state: item });
        } else if(isSelfPaced) {
          navigate("/self-paced/drill", { state: item });
        }
      } else {
        openExternalUrl({
          url: item.LaunchUrl || item.ContentObject?.LaunchUrl,
          contentObjectType:
            item.ContentObjectType ||
            item.ContentObject?.ContentObjectType ||
            item.Type,
          testType: payload?.testtype,
          dispatch,
          onRefreshModalOpen: () => {
            handleRefreshModalOpen(item);
          },
        });
      }
    }
  };

  const formatCompletedOn = (value: any) =>
    value ? formatToLocalDate(value, "MM/DD/YY h:mm A") : value;

  return (
    <>
      {type !== "TopicFocus" ? (
        <div className={classNames("progress-summary-mobile-test-wrapper")}>
          {data && Array.isArray(data) && data.length > 0 ? (
            data.map((item: any, index: number) => (
              <div
                key={index}
                className={classNames({
                  "progress-summary-mobile-items": true,
                  highlight: index % 2 === 0,
                })}
              >
                <div>
                  <Typography variant="h8Med" label={getTitle(type)} />
                  <Typography
                    styles={
                      type !== "Attendance"
                        ? "progress-summary-mobile-link-text"
                        : undefined
                    }
                    variant="h8Med"
                    label={item.Name}
                    onClick={() => handleItemClick(item, type)}
                  />
                </div>
                <div>
                  <Typography variant="h8Med" label="Due Date" />
                  <Typography
                    styles="progress-summary-mobile-normal-text"
                    variant="h8Book"
                    label={item.DueDate || item.ClassDate}
                  />
                </div>
                <div>
                  <Typography variant="h8Med" label="Date Completed" />
                  <Typography
                    styles="progress-summary-mobile-normal-text"
                    variant="h8Book"
                    label={
                      formatCompletedOn(item.DateCompleted) ||
                      formatCompletedOn(item.TimeCompleted) ||
                      formatCompletedOn(item.TimeEntered)
                    }
                  />
                </div>
                <div>
                  <Typography variant="h8Med" label="Status" />
                  <Label
                    backgroundColor={getMatchColor(item["Status"])}
                    dynamicTextColor={
                      item['Status'].toLowerCase() === "locked"
                        ? "#ffffff"
                        : "#000000"
                    }
                    label={item.Status}
                  />
                </div>
              </div>
            ))
          ) : (
            <div className="empty-data-wrapper">
              <Typography variant="h8Book" label="No data available" />
            </div>
          )}
        </div>
      ) : (
        <div className={classNames("progress-summary-mobile-test-wrapper")}>
          {isFetching ? (
            <Spinner theme={theme} />
          ) : data && Array.isArray(data) && data.length > 0 ? (
            data.map((item: any, index: number) => (
              <div
                key={index}
                className={classNames({
                  "progress-summary-mobile-items": true,
                  highlight: index % 2 === 0,
                })}
              >
                <div>
                  <Typography variant="h8Med" label={getTitle(type)} />
                  <Typography variant="h8Med" label={item.Name} />
                </div>
                {item.Status !== "Not Attended" ? (
                  <div className={classNames("attended-class-wrapper")}>
                    <div className={classNames("inner-wrapper")}>
                      <Typography variant="h8Med" label="Class Date" />
                      <Typography
                        styles="progress-summary-mobile-normal-text"
                        variant="h8Book"
                        label={
                          (dayjs(item.DueDate).isValid()
                            ? dayjs(item.DueDate).format("MM/DD/YY h:mm A")
                            : item.DueDate) ||
                          (dayjs(item.ClassDate).isValid()
                            ? dayjs(item.ClassDate).format("MM/DD/YY h:mm A")
                            : item.ClassDate)
                        }
                      />
                    </div>
                    <div className={classNames("inner-wrapper")}>
                      <Typography variant="h8Med" label="Instructor Name" />
                      <Typography
                        styles="progress-summary-mobile-normal-text"
                        variant="h8Book"
                        label={
                          (item.NearFutureSegments &&
                            Array.isArray(item.NearFutureSegments) &&
                            item.NearFutureSegments.length > 0 &&
                            item.NearFutureSegments[0].InstructorName) ||
                          "Unknown"
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <InstructorMobile item={item} />
                )}
                <div>
                  <Typography variant="h8Med" label="Date Completed" />
                  <Typography
                    styles="progress-summary-mobile-normal-text"
                    variant="h8Book"
                    label={
                      formatCompletedOn(item.DateCompleted) ||
                      formatCompletedOn(item.TimeCompleted) ||
                      formatCompletedOn(item.TimeEntered)
                    }
                  />
                </div>
                <div className="status-wrapper">
                  <Typography variant="h8Med" label="Status" />
                  {item.Status !== "Not Attended" || item.IsAddedToHome ? (
                    <Label
                      backgroundColor={getMatchColor(item["Status"])}
                      dynamicTextColor={
                        item["Status"].toLowerCase() === "locked"
                          ? "#ffffff"
                          : "#000000"
                      }
                      label={item.Status}
                    />
                  ) : (
                    <InstructorListButtonMobile item={item} key={index} />
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="empty-data-wrapper">
              <Typography variant="h8Book" label="No data available" />
            </div>
          )}
        </div>
      )}
      <RefreshModal
        isOpen={isRefreshModalOpen.isOpen}
        handleOnClose={handleRefreshModalClose}
        theme={theme}
        contentObjectType={
          isRefreshModalOpen.data !== ""
            ? isRefreshModalOpen.data
            : contentObjectType
        }
        isFetching={isFetching}
        handleRefresh={handleRefresh}
      />
    </>
  );
};
